import dayjs from "dayjs";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import {
  BETWEEN,
  DATE,
  GTE,
  LIKE,
  LTE,
  STRING,
  BOOL,
  EQ,
  SIMILARTO,
  CONTAINS,
} from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import DatePicker from "../../framework/date/DatePicker";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import SelectInput from "../../framework/SelectInput";
import ChipInputSelect from "../../framework/ChipInputSelect";
import { useDispatch } from "react-redux";
import Checkbox from "../../framework/Checkbox";
import { _ } from "lodash";

import { fetchBranches } from "../../services/branch-service";
import { listBranchGroup } from "../../services/branch-group-services";
const FrequencySlabReportFilter = ({
  onFilterShow,
  applyFilterOnMount = true,
  reportRef,
}) => {
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [slabName, setSlabName] = useState("");
  const [status, setStatus] = useState("All");
  const [limit, setLimit] = useState(100);
  const filterSlabNameRef = useRef();
  const filterStartDateRef = useRef();
  const filterEndDateRef = useRef();
  const filterStatusRef = useRef();
  const filterBranchRef = useRef();
  const filterLimitRef = useRef();
  const [orderBy] = useState([{ fieldName: "createdTime", orderType: "desc" }]);

  const statusOptions = useMemo(() => ["All", "Active", "Inactive"], []);
  const dispatch = useDispatch();
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const { setCurrentComponent } = useContext(GlobalContext);

  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      const _startDate = startDate?.format("DD/MM/YYYY");
      const _endDate = endDate?.format("DD/MM/YYYY");
      if (_startDate && !_endDate) {
        filters.push(createFilterParam("createdTime", _startDate, DATE, GTE));
      }
      if (!_startDate && _endDate) {
        filters.push(createFilterParam("createdTime", _endDate, DATE, LTE));
      }
      if (_startDate && _endDate) {
        filters.push(
          createFilterParam(
            "createdTime",
            { start: _startDate, end: _endDate },
            DATE,
            BETWEEN
          )
        );
      }
      if (slabName) {
        filters.push(createFilterParam("programName", slabName, STRING, LIKE));
      }
      if (status && status !== "All") {
        filters.push(
          createFilterParam(
            "deleted",
            status === "Active" ? false : true,
            BOOL,
            EQ
          )
        );
      }

      if (selectedBranches && selectedBranches?.length > 0) {
        const formattedBranches = selectedBranches.reduce((acc, branch) => {
          if (Array.isArray(branch)) {
            acc.push(...branch);
          } else if (branch) {
            acc.push(branch);
          }
          return acc;
        }, []);
        filters.push(
          createFilterParam(
            "branchCode",
            formattedBranches.map((b) => b.code).join("|"),
            STRING,
            CONTAINS
          )
        );
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      if (orderBy) {
        result.orderBy = orderBy;
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }

      reportRef.current?.applyFilters(result);
    },
    [
      endDate,
      limit,
      orderBy,
      slabName,
      reportRef,
      selectedBranches,
      startDate,
      status,
    ]
  );
  const handleBranchChange = useCallback((event, newValue) => {
    if (!newValue) return;
    //const branches = newValue.map((branch) => branch.branchCode);
    setSelectedBranches(newValue);
  }, []);

  const fetchAllBranchData = useCallback(async () => {
    const _branch = await fetchBranches();
    const _branchGroup = await listBranchGroup();
    const retailerCode = localStorage.getItem("retailerCode");
    const filteredBranches = (_branch?.result?.rows || []).filter(
      (branch) => branch.branchCode !== retailerCode
    );
    const mergedBranches = [...(_branchGroup?.rows || []), ...filteredBranches];
    setBranches(mergedBranches);
  }, []);

  const filterListener = useCallback(
    (event) => {
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey;
      if (ctrl && key === "enter") {
        applyFilterHandler(true);
        return;
      }
    },
    [applyFilterHandler]
  );
  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="fsr_filter_start_date"
            label="Start Date"
            inputRef={filterStartDateRef}
            onChange={(value) => setStartDate(value)}
            onFocus={() => setCurrentComponent("fsr_filter_start_date")}
            value={startDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "fsr_filter_start_date",
                down: "fsr_filter_end_date",
                left: "fsr_filter_start_date",
                right: "fsr_filter_end_date",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="fsr_filter_end_date"
            label="End Date"
            inputRef={filterEndDateRef}
            onChange={(value) => setEndDate(value)}
            onFocus={() => {
              executeScroll(filterEndDateRef);
              setCurrentComponent("fsr_filter_end_date");
            }}
            value={endDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "fsr_filter_start_date",
                down: "fsr_filter_slab_name",
                left: "fsr_filter_start_date",
                right: "fsr_filter_slab_name",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="fsr_filter_slab_name"
            label="Slab Name"
            ref={filterSlabNameRef}
            value={slabName}
            onChange={(e) => setSlabName(e.target.value)}
            onFocus={() => {
              executeScroll(filterSlabNameRef);
              setCurrentComponent("fsr_filter_slab_name");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "fsr_filter_end_date",
                down: "fsr_filter_status",
                left: "fsr_filter_end_date",
                right: "fsr_filter_status",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="fsr_filter_status"
            label="Status"
            ref={filterStatusRef}
            options={statusOptions.map((status) => ({
              value: status,
              label: status,
            }))}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            isOptionEqualToValue={(option, value) => option.value === value}
            value={status}
            onChange={(e, option) => setStatus(option?.value || "")}
            onFocus={() => {
              executeScroll(filterStatusRef);
              setCurrentComponent("fsr_filter_status");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "fsr_filter_slab_name",
              down: "fsr_branches",
              left: "fsr_filter_slab_name",
              right: "fsr_branches",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <ChipInputSelect
            id="fsr_branches"
            label="Branches"
            multiple={true}
            ref={filterBranchRef}
            groupBy={(option) =>
              option?.branchGroupId ? "Branch Groups" : "Branches"
            }
            isOptionEqualToValue={(option, value) => {
              return option === value;
            }}
            getOptionLabel={(option) =>
              `${option?.branchName || option?.name || ""}- ${
                option?.branchGroupId ? "BG" : "BR"
              }`
            }
            value={selectedBranches}
            onChange={handleBranchChange}
            filterSelectedOptions={true}
            blurOnSelect={false}
            disableCloseOnSelect={true}
            limitTags={4}
            onFocus={() => {
              executeScroll(filterBranchRef);
              setCurrentComponent("fsr_branches");
            }}
            options={branches}
            sx={{
              width: "500px",
              padding: "0px",
              marginRight: "5px",
              boxSizing: "border-box",
            }}
            renderGroup={(params) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: "5px",
                  }}
                  key={params.key}
                >
                  <div
                    style={{
                      height: "30px",
                      width: "100%",
                      background: "#e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {params?.group}
                  </div>
                  <div>{params.children}</div>
                </div>
              );
            }}
            renderOption={(props, option, { selected }) => {
              return (
                <div
                  {...props}
                  style={{
                    display: "flex",
                    height: "40px",
                    width: "100%",
                    padding: "1px 5px",
                    gap: "5px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItem: "center",
                    }}
                  >
                    <Checkbox checked={selected} />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItem: "center",
                      marginRight: "5px",
                    }}
                  >
                    {option?.branchGroupId ? "BG |" : "BR |"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItem: "center",
                      gap: "5px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: "15px",
                        width: "100%",
                      }}
                    >
                      {option?.name || option?.code} (
                      {option?.code || option?._id})
                    </div>
                  </div>
                </div>
              );
            }}
            markers={JSON.stringify({
              up: "fsr_filter_status",
              left: "fsr_filter_status",
              right: "fsr_filter_limit",
              down: "fsr_filter_limit",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="fsr_filter_limit"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("fsr_filter_limit");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "fsr_branches",
                down: "fsr_apply_filter_button",
                left: "fsr_branches",
                right: "fsr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="fsr_apply_filter_button"
            label="Apply"
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => applyFilterHandler(true)}
            markers={JSON.stringify({
              up: "fsr_filter_limit",
              left: "fsr_filter_limit",
              down: "fsr_apply_filter_button",
              right: "fsr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  useEffect(() => {
    fetchAllBranchData();
  }, [fetchAllBranchData]);

  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  useEffect(() => {
    if (onFilterShow) {
      filterStartDateRef?.current?.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) {
      document.addEventListener("keyup", filterListener);
    }
    return () => {
      document.removeEventListener("keyup", filterListener);
    };
  }, [filterListener, onFilterShow]);

  return (
    <Filters
      title="Frequency Slab Report Filters"
      filterContent={filterContent}
      filterFooter={filterFooter}
    />
  );
};

export default FrequencySlabReportFilter;
